import { Cross2Icon } from "@radix-ui/react-icons";
import { COMPACT_INPUT } from "components/Inputs/compact";
import { KindOptions } from "components/MessageTemplates/TriggerDialogs";
import { LABEL } from "components/ProfileDropdown/settingsStyles";
import { useAutomations, useReference } from "./hooks";
import {
  defaultTrigger,
  isTaskKind,
  kindOf,
  pickerKinds,
  TASK_STATUS_CHANGED,
  TASK_STATUSES,
} from "./kinds";
import { ChoiceGroup, ChoiceSelect, FieldRow } from "./parts";
import type { ReferenceData } from "./reference";
import type {
  PersonKey,
  Trigger,
  TriggerFilter,
  TriggerFilterField,
} from "./types";

const FILTER_FIELDS: { value: TriggerFilterField; label: string }[] = [
  { value: "name_contains", label: "Name contains" },
  { value: "made_by", label: "Made by" },
  { value: "assigned_to", label: "Assigned to" },
  { value: "about", label: "About" },
];

function newFilter(field: TriggerFilterField): TriggerFilter {
  switch (field) {
    case "name_contains":
      return { field, value: "" };
    case "made_by":
      return { field, value: "any" };
    case "assigned_to":
    case "about":
      return { field, value: null };
  }
}

const ROLE_GROUPS: { role: string; label: string }[] = [
  { role: "admin", label: "Admins" },
  { role: "tutor", label: "Tutors" },
  { role: "parent", label: "Parents" },
  { role: "student", label: "Students" },
];

/** Tadpole's people as select groups, by role. */
export function peopleGroups(ref: ReferenceData): ChoiceGroup<PersonKey>[] {
  return ROLE_GROUPS.map(({ role, label }) => ({
    label,
    options: ref.people
      .filter((p) => p.role === role)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ value: p.key, label: p.name })),
  })).filter((g) => g.options.length > 0);
}

/**
 * "A task is finished" and "A task's status changes": the status, then
 * filters by name, maker, assignee and about, one of each.
 */
function TaskTriggerFields({
  trigger,
  onChange,
}: {
  trigger: Trigger;
  onChange: (next: Trigger) => void;
}) {
  const ref = useReference();
  const { automations } = useAutomations();
  const filters = trigger.filters;
  const setFilters = (next: TriggerFilter[]) =>
    onChange({ ...trigger, filters: next });
  const replace = (i: number, filter: TriggerFilter) =>
    setFilters(filters.map((f, j) => (j === i ? filter : f)));
  const used = (field: TriggerFilterField) =>
    filters.some((f) => f.field === field);
  const unused = FILTER_FIELDS.find((f) => !used(f.value));

  const valueControl = (filter: TriggerFilter, i: number) => {
    switch (filter.field) {
      case "name_contains":
        return (
          <input
            type="text"
            className={`${COMPACT_INPUT} w-56`}
            aria-label="Words in the task name"
            placeholder="Words in the task name"
            value={filter.value}
            onChange={(e) =>
              replace(i, { field: "name_contains", value: e.target.value })
            }
          />
        );
      case "made_by":
        return (
          <ChoiceSelect
            label="Made by"
            value={filter.value}
            options={[
              { value: "any", label: "Any automation" },
              ...automations.map((a) => ({ value: a.id, label: a.name })),
            ]}
            onChange={(value) => replace(i, { field: "made_by", value })}
            className="min-w-[14rem] flex-1"
          />
        );
      case "assigned_to":
      case "about":
        return (
          <ChoiceSelect<PersonKey | null>
            label={filter.field === "about" ? "About" : "Assigned to"}
            value={filter.value}
            options={peopleGroups(ref)}
            placeholder="Pick a person"
            searchable
            onChange={(value) =>
              replace(i, { field: filter.field, value } as TriggerFilter)
            }
            className="min-w-[14rem] flex-1"
          />
        );
    }
  };

  return (
    <>
      {trigger.kind === TASK_STATUS_CHANGED && (
        <FieldRow label="Changes to">
          <ChoiceSelect
            label="Changes to"
            value={String(trigger.options.to ?? "any")}
            options={[{ value: "any", label: "Any status" }, ...TASK_STATUSES]}
            onChange={(to) =>
              onChange({ ...trigger, options: { ...trigger.options, to } })
            }
          />
        </FieldRow>
      )}
      <div className="mt-3">
        <p className={LABEL}>Only when</p>
        {filters.length === 0 && (
          <p className="mt-1 text-sm text-gray-600">Every task counts.</p>
        )}
        {filters.map((filter, i) => (
          <div
            key={i}
            className="mt-1 flex flex-wrap items-center gap-2 text-sm"
          >
            <ChoiceSelect
              label="Filter"
              value={filter.field}
              options={FILTER_FIELDS.map((f) => ({
                ...f,
                isDisabled: f.value !== filter.field && used(f.value),
              }))}
              onChange={(field) =>
                field !== filter.field && replace(i, newFilter(field))
              }
              className="w-44"
            />
            {valueControl(filter, i)}
            <button
              type="button"
              className="rounded-md p-1 text-gray-500 hover:bg-red-50 hover:text-red-800"
              aria-label="Take this filter out"
              onClick={() => setFilters(filters.filter((_, j) => j !== i))}
            >
              <Cross2Icon />
            </button>
          </div>
        ))}
        {unused && (
          <button
            type="button"
            className="mt-2 block text-xs font-semibold text-green-700 hover:underline"
            onClick={() => setFilters([...filters, newFilter(unused.value)])}
          >
            + Add a filter
          </button>
        )}
      </div>
    </>
  );
}

/**
 * The trigger: what starts it (the backend's kinds, then the task kinds),
 * and that kind's own choices, with the template trigger dialog's own
 * controls, leaving out the kind's own timing (the first steps carry it).
 * Picking another kind starts from its defaults. boxed: a card in an AND box
 * of two or more, whose kinds it names as such a box does.
 */
export default function TriggerPicker({
  value,
  onChange,
  label = "Trigger type",
  boxed = false,
}: {
  value: Trigger;
  onChange: (next: Trigger) => void;
  label?: string;
  boxed?: boolean;
}) {
  const ref = useReference();
  const kind = kindOf(ref, value.kind);
  return (
    <div>
      <FieldRow label={label} className="">
        <ChoiceSelect
          label={label}
          value={value.kind}
          options={pickerKinds(ref, boxed).map((k) => ({
            value: k.kind,
            label: k.label,
            hint: k.description,
          }))}
          onChange={(next) =>
            next !== value.kind && onChange(defaultTrigger(ref, next))
          }
          placeholder="Pick what starts it"
          searchable
          className="w-full"
        />
      </FieldRow>
      {kind?.inCatalog && (
        <KindOptions
          kind={value.kind}
          options={value.options}
          set={(changes) =>
            onChange({ ...value, options: { ...value.options, ...changes } })
          }
          catalog={ref.catalog}
          timing={false}
        />
      )}
      {isTaskKind(value.kind) && (
        <TaskTriggerFields trigger={value} onChange={onChange} />
      )}
    </div>
  );
}

/**
 * The kinds as cards, as the template trigger dialog first shows them: one
 * click picks what starts a new automation. boxed: for a new card in an AND
 * box, whose kinds it names as such a box does.
 */
export function TriggerKindGrid({
  onPick,
  boxed = false,
}: {
  onPick: (kind: string) => void;
  boxed?: boolean;
}) {
  const ref = useReference();
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {pickerKinds(ref, boxed).map((k) => (
        <button
          key={k.kind}
          type="button"
          onClick={() => onPick(k.kind)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-[#98acfc] hover:bg-[#98acfc]/5"
        >
          <span className="block text-sm font-semibold text-gray-900">
            {k.label}
          </span>
          <span className="block text-xs text-gray-600">{k.description}</span>
        </button>
      ))}
    </div>
  );
}
